import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import type {
  OrderDetailPerspectiveView,
  OrderFlag,
} from '@/api/hooks/useOrderDetails'
import type { DataPerspective } from '@/api/hooks/useOrders'
import { OrderFlagChips } from '@/components/Display/OrderFlagChips'
import {
  integrationStatusConfig,
  tripStatusConfig,
} from '@/pages/Dashboard/panels/OrdersPanel/OrdersCellRenderers'
import {
  ArrowLeftOutlined,
  CompressOutlined,
  ExpandOutlined,
  FileSearchOutlined,
  HistoryOutlined,
  LockOutlined,
  MailOutlined,
} from '@ant-design/icons'
import { BBDTag, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Button, Select, Tag, Tooltip } from 'antd'
import { useNavigate } from 'react-router-dom'

interface OrderDetailsHeaderProps {
  order: OrderDetailPerspectiveView
  dataPerspective: DataPerspective
  onDataPerspectiveChange: (perspective: DataPerspective) => void
  onHistoryClick: () => void
  onEmailsClick: () => void
  isExpanded: boolean
  onExpandToggle: () => void
  onBackClick: () => void
  onChangeRequestClick?: (changeId: string) => void
  /** The row's own log and the group it belongs to. Both sit at the top
   * level of OrderDetailRow, shared by all three perspectives, so they come
   * in alongside `order` rather than off it. */
  log?: CrossroadsLog | null
  logGroupId?: string | null
  onLogClick?: (log: CrossroadsLog, logGroupId: string | null) => void
  flags?: OrderFlag[]
}

export function OrderDetailsHeader({
  order,
  dataPerspective,
  onDataPerspectiveChange,
  onHistoryClick,
  onEmailsClick,
  isExpanded,
  onExpandToggle,
  onBackClick,
  onChangeRequestClick,
  log,
  logGroupId,
  onLogClick,
  flags = [],
}: OrderDetailsHeaderProps) {
  const navigate = useNavigate()
  const orderNumber =
    order.internal_order_number || order.partner_order_number || '-'

  const integrationStatus = order.status
  const statusConfigKey =
    integrationStatus === 'requested' && !order.pending_change_id
      ? 'order_requested'
      : integrationStatus
  const statusConfig = statusConfigKey
    ? integrationStatusConfig[statusConfigKey]
    : null

  const tripStatus = order.trip_status
  const tripConfig = tripStatus ? tripStatusConfig[tripStatus] : null

  // Mirror the orders grid: the status chip opens the row's log, and a
  // change-request status opens the change request drawer instead.
  const isChangeRequestClickable =
    statusConfig?.isChangeRequest &&
    order.pending_change_id &&
    onChangeRequestClick

  let handleStatusClick: (() => void) | undefined
  if (isChangeRequestClickable) {
    handleStatusClick = () => onChangeRequestClick?.(order.pending_change_id!)
  } else if (log && onLogClick) {
    handleStatusClick = () => onLogClick(log, logGroupId ?? null)
  }

  // Build subtitle parts (PO only, Partner Order moved to Order Details card)
  const subtitleParts: string[] = []
  if (order.po) subtitleParts.push(`PO: ${order.po}`)

  return (
    <Vertical
      style={{
        flexShrink: 0,
      }}
      gap={10}
    >
      {/* Navigation row: Back link | Order Navigation */}
      <Horizontal alignItems='center' justifyContent='space-between'>
        <Button
          type='link'
          size='small'
          icon={<ArrowLeftOutlined />}
          onClick={onBackClick}
          style={{ padding: 0, height: 'auto', lineHeight: 1 }}
        >
          Back to Orders
        </Button>
      </Horizontal>

      {/* Top row: Order number + status | Data Format */}
      <Horizontal alignItems='center' justifyContent='space-between'>
        <Horizontal alignItems='center' gap={16}>
          <Texto
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
            }}
          >
            Order #{orderNumber}
          </Texto>
          {subtitleParts.length > 0 && (
            <Texto style={{ fontSize: 13 }}>{subtitleParts.join(' • ')}</Texto>
          )}
          <Horizontal alignItems='center' gap={2}>
            {order.locked && (
              <Tag color='warning'>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <LockOutlined />
                  {order.locked_by ? `Locked by ${order.locked_by}` : 'Locked'}
                </span>
              </Tag>
            )}
            {tripConfig && (
              <Tag color={tripConfig.color}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {tripConfig.icon} Trip Status: {tripConfig.label}
                </span>
              </Tag>
            )}
            {statusConfig && (
              <BBDTag
                color={statusConfig.color}
                className={handleStatusClick ? 'cursor-pointer' : undefined}
                onClick={handleStatusClick}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {statusConfig.icon} Integration Status: {statusConfig.label}
                </span>
              </BBDTag>
            )}
            <OrderFlagChips flags={flags} />
          </Horizontal>
        </Horizontal>

        <Horizontal alignItems='center' gap={10}>
          <Horizontal alignItems='center' gap={6}>
            <Texto style={{ fontSize: 12 }}>Data Format:</Texto>
            <Select
              value={dataPerspective}
              onChange={onDataPerspectiveChange}
              style={{ width: 130 }}
              size='small'
              variant='outlined'
            >
              <Select.Option value='internal_order'>
                Internal Data
              </Select.Option>
              <Select.Option value='gravitate_order'>Gravitate</Select.Option>
              <Select.Option value='partner_order'>Partner</Select.Option>
            </Select>
          </Horizontal>
          <Button
            size='small'
            icon={<HistoryOutlined />}
            onClick={onHistoryClick}
          >
            Order History
          </Button>
          <Button size='small' icon={<MailOutlined />} onClick={onEmailsClick}>
            Emails
          </Button>
          <Button
            size='small'
            icon={<FileSearchOutlined />}
            disabled={!order.internal_order_number}
            onClick={() => {
              // The connection logs view filters on origin_order_number, which
              // maps to internal_order_number on every perspective.
              if (!order.internal_order_number) return
              navigate(
                `/integration-management/connection-logs?order=${encodeURIComponent(order.internal_order_number)}`,
              )
            }}
          >
            View Logs
          </Button>
          <Tooltip title={isExpanded ? 'Collapse view' : 'Expand view'}>
            <Button
              size='small'
              icon={isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={onExpandToggle}
            />
          </Tooltip>
        </Horizontal>
      </Horizontal>
    </Vertical>
  )
}
